import React from "react";

// Drop-in replacement for the abandoned `react-bootstrap-table` package, removed
// during the React 19 upgrade (it peer-locked to React 15/16 and relied on APIs
// dropped in React 18/19). This reproduces only the subset of the
// BootstrapTable / TableHeaderColumn API the app actually uses, and emits the same
// `.react-bs-table*` DOM structure so the existing CSS (modern-theme.css, style.css,
// app.css) styles it unchanged. Pagination/search/sorting are intentionally omitted —
// the app drives paging via its own <Pagination> component and paginates server-side.
//
// Supported BootstrapTable props: data, options.onRowClick(row, colIdx, rowIdx, e),
//   trClassName (string | (row, rowIdx) => string), className, tableHeaderClass,
//   striped, hover, bordered. (version/pagination/search/multiColumnSearch: ignored.)
// Supported TableHeaderColumn props: dataField, dataFormat(cell, row, extra, rowIdx),
//   isKey, width, dataAlign, hidden, columnClassName (string | (cell, row, rowIdx) => string),
//   plus children as the header label.

// Config-only marker component. BootstrapTable reads its props/children; it never
// renders on its own.
export function TableHeaderColumn() {
  return null;
}

function normalizeWidth(w) {
  if (w == null) return undefined;
  return typeof w === "number" || /^\d+$/.test(String(w)) ? `${parseInt(w, 10)}px` : String(w);
}

function columnsFromChildren(children) {
  return React.Children.toArray(children)
    .filter(child => React.isValidElement(child))
    .map(child => {
      const p = child.props || {};
      return {
        dataField: p.dataField,
        dataFormat: p.dataFormat,
        isKey: !!p.isKey,
        width: normalizeWidth(p.width),
        dataAlign: p.dataAlign,
        hidden: !!p.hidden,
        columnClassName: p.columnClassName,
        header: p.children,
      };
    });
}

function resolveClass(value, cell, row, rowIndex) {
  if (typeof value === "function") return value(cell, row, rowIndex) || "";
  return value || "";
}

export function BootstrapTable(props) {
  const { data = [], children, className = "", tableHeaderClass = "", trClassName, options = {}, striped, hover, bordered } = props;
  // Resolve the key column from ALL columns (a hidden isKey column, e.g. task-review's
  // "id", still gives stable row keys) but render only the visible ones.
  const allColumns = columnsFromChildren(children);
  const keyCol = allColumns.find(c => c.isKey);
  const columns = allColumns.filter(c => !c.hidden);
  const onRowClick = options.onRowClick;

  const rowKey = (row, i) => {
    const k = keyCol && keyCol.dataField ? row[keyCol.dataField] : undefined;
    return k != null ? k : i;
  };

  // Single auto-layout table (thead + tbody together): header and body columns align
  // natively without JS width-syncing, and the declared column widths act as soft hints
  // that the browser honours only when there's room — so a wide set of columns fits the
  // container and wraps instead of forcing a horizontal scroll (the two-table + fixed-layout
  // approach did the opposite). The CSS targets `.react-bs-table table thead/tbody`.
  // `tableHeaderClass` styles the header only (react-bootstrap-table applied it there,
  // not to the whole table) — putting it on <table> bled the `.primary` teal fill onto
  // every cell.
  const tableClass = ["table", striped && "table-striped", hover && "table-hover", bordered && "table-bordered"]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`react-bs-table-container ${className}`.trim()}>
      <div className="react-bs-table">
        <table className={tableClass} style={{ width: "100%" }}>
          <colgroup>
            {columns.map((c, i) => (
              <col key={i} style={c.width ? { width: c.width } : undefined} />
            ))}
          </colgroup>
          <thead className={tableHeaderClass || undefined}>
            <tr>
              {columns.map((c, i) => (
                <th key={i} style={c.dataAlign ? { textAlign: c.dataAlign } : undefined}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr
                key={rowKey(row, i)}
                className={resolveClass(trClassName, row, row, i)}
                onClick={onRowClick ? e => onRowClick(row, undefined, i, e) : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}>
                {columns.map((c, ci) => {
                  const cell = c.dataField ? row[c.dataField] : undefined;
                  const content = c.dataFormat ? c.dataFormat(cell, row, undefined, i) : cell;
                  return (
                    <td
                      key={ci}
                      className={resolveClass(c.columnClassName, cell, row, i)}
                      style={c.dataAlign ? { textAlign: c.dataAlign } : undefined}>
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BootstrapTable;
